/**
 * How many recipient names fit on one line of the conversation header.
 *
 * Kept apart from the component because it is arithmetic, not rendering: the
 * widths come from one layout read, and every resize after that is this
 * function again over the same numbers.
 */

/** A name is always shown, however narrow the drawer is dragged. */
export const MIN_VISIBLE_RECIPIENTS = 1;

export interface RecipientLineWidths {
  /** "CC: " — the label that opens the line. */
  lead: number;
  /** Each name including the ", " that follows it. */
  items: number[];
  /** The widest "+N more" this list could need. */
  more: number;
}

/**
 * Two passes, because the answer changes its own question: a list that does not
 * fit has to give up room for the "+N more" that says so, and that label is
 * only needed once something is hidden.
 *
 * Measured in intrinsic widths rather than positions, so the caller can keep
 * the numbers from the one render where every name was on screen and re-run
 * this on resize without putting them back.
 */
export const countRecipientsThatFit = (
  { lead, items, more }: RecipientLineWidths,
  lineWidth: number
): number => {
  if (items.length === 0) return 0;
  // Nothing measured yet (jsdom, or a row that has not been laid out): show
  // everything rather than cutting to a width we do not know.
  if (lineWidth <= 0) return items.length;

  const fitting = (reserved: number) => {
    let used = lead + reserved;
    let count = 0;

    while (count < items.length && used + items[count] <= lineWidth) {
      used += items[count];
      count += 1;
    }

    return count;
  };

  if (fitting(0) === items.length) return items.length;

  return Math.max(fitting(more), MIN_VISIBLE_RECIPIENTS);
};
